import React, { useEffect, useState } from "react";
import {
  Form,
  Modal,
  Select,
  Input,
  InputNumber,
  Checkbox,
  Spin,
  FormInstance,
} from "antd";
import dayjs from "dayjs";
import { FrontDeskBooking } from "../../../types";
import { frontDeskService } from "../../../services/adminService";
import { PAYMENT_METHOD_OPTIONS } from "../../../constants/paymentMethods";

interface CheckInModalProps {
  open: boolean;
  onCancel: () => void;
  form: FormInstance;
  onSubmit: (values: any) => void;
  selectedCheckInBooking: FrontDeskBooking | null;
  loading?: boolean;
}

const money = (n: number) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const CheckInModal: React.FC<CheckInModalProps> = ({
  open,
  onCancel,
  form,
  onSubmit,
  selectedCheckInBooking,
  loading,
}) => {
  const [breakdown, setBreakdown] = useState<{
    grossTotal: number;
    paidToDate: number;
    balanceDue: number;
    isGroup: boolean;
  } | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  const booking = selectedCheckInBooking;
  const balance = breakdown ? breakdown.balanceDue : 0;
  // Collection at check-in is opt-in for EVERY booking, walk-ins included:
  // the tick below is the only thing that can move money here.
  const hasRemaining = balance > 0;
  const isGroupStay = Boolean(breakdown?.isGroup);

  useEffect(() => {
    if (!open || !booking) return;

    // A group is one account: the balance box (and the amount collected by
    // "pay remaining") covers every room of the group, not just this one.
    const breakdownId = booking.group_id
      ? `group-${booking.group_id}`
      : booking.id;
    setBreakdown(null);
    setBreakdownLoading(true);
    form.resetFields();
    frontDeskService
      .getInvoiceBreakdown(breakdownId, "contract")
      .then((res: any) => {
        const d = res?.data?.data || res?.data;
        if (d) {
          setBreakdown({
            grossTotal: Number(d.grossTotal || 0),
            paidToDate: Number(d.paidToDate || 0),
            balanceDue: Number(d.balanceDue || 0),
            isGroup: d.is_group === true,
          });
        }
      })
      .catch(() => setBreakdown(null))
      .finally(() => setBreakdownLoading(false));

    form.setFieldsValue({
      pay_remaining: false,
      // Prefill from the booking so an early check-in fee recorded at
      // create/edit time is not wiped when staff confirm the check-in.
      early_check_in:
        Boolean((booking as any).early_check_in) ||
        Number((booking as any).early_check_in_fee || 0) > 0,
      early_check_in_fee: Number((booking as any).early_check_in_fee || 0),
    });
  }, [open, booking, form]);

  const nights = booking
    ? Math.max(
        1,
        Math.round(
          (new Date(booking.check_out_date).getTime() -
            new Date(booking.check_in_date).getTime()) /
            86400000,
        ),
      )
    : 1;

  return (
    <Modal
      open={open}
      title={`Check-In${booking ? ` - ${booking.guest_name}` : ""}`}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      okText="Confirm Check-In"
      width={640}
    >
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        {booking && (
          <div
            style={{
              marginBottom: 16,
              padding: "10px 12px",
              backgroundColor: "#f5f5f5",
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            <strong>{booking.room_number || "Room not allocated"}</strong>
            {booking.room_type_name ? ` · ${booking.room_type_name}` : ""}
            <div style={{ marginTop: 4 }}>
              <strong>Check-in:</strong>{" "}
              {dayjs(booking.check_in_date).format("ddd, DD MMM YYYY")} ·{" "}
              <strong>Check-out:</strong>{" "}
              {dayjs(booking.check_out_date).format("ddd, DD MMM YYYY")} ·{" "}
              {nights} {nights === 1 ? "night" : "nights"} ·{" "}
              {booking.number_of_guests} guest(s)
            </div>
          </div>
        )}

        <div
          style={{
            marginBottom: 16,
            padding: "12px 14px",
            backgroundColor: "#fffbe6",
            border: "1px solid #ffe58f",
            borderRadius: 8,
          }}
        >
          <div
            style={{
              fontWeight: 600,
              color: "#d46b08",
              fontSize: 14,
              marginBottom: 6,
            }}
          >
            {isGroupStay ? "Group Balance" : "Balance"}
          </div>
          {breakdownLoading ? (
            <Spin size="small" />
          ) : breakdown ? (
            <div style={{ fontSize: 13, color: "#614700", lineHeight: 1.9 }}>
              <div>
                {isGroupStay ? "Group total" : "Gross"}:{" "}
                <strong>LKR {money(breakdown.grossTotal)}</strong>
              </div>
              <div>
                Paid: <strong>LKR {money(breakdown.paidToDate)}</strong>
              </div>
              <div>
                Balance due:{" "}
                <strong style={{ color: balance > 0 ? "#cf1322" : "#389e0d" }}>
                  LKR {money(balance)}
                </strong>
              </div>
              {isGroupStay && (
                <div style={{ marginTop: 4, fontSize: 12, color: "#874d00" }}>
                  Covers every room of this group booking.
                </div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: "#614700" }}>
              Total:{" "}
              <strong>LKR {money(Number(booking?.total_price || 0))}</strong>
            </div>
          )}

          {hasRemaining && (
            <div style={{ marginTop: 10 }}>
              <Form.Item
                name="pay_remaining"
                valuePropName="checked"
                noStyle
                initialValue={false}
              >
                <Checkbox style={{ color: "#d46b08", fontWeight: 600 }}>
                  {isGroupStay
                    ? "Collect group balance now"
                    : "Collect remaining balance now"}
                </Checkbox>
              </Form.Item>

              <Form.Item
                shouldUpdate={(prev, curr) =>
                  prev.pay_remaining !== curr.pay_remaining
                }
                noStyle
              >
                {({ getFieldValue }) =>
                  getFieldValue("pay_remaining") ? (
                    <div
                      style={{
                        marginTop: 12,
                        borderTop: "1px dashed #ffe58f",
                        paddingTop: 12,
                      }}
                    >
                      <Form.Item
                        name="payment_method"
                        label="Payment Method"
                        rules={[{ required: true, message: "Select a method" }]}
                        initialValue="cash"
                      >
                        <Select options={PAYMENT_METHOD_OPTIONS} />
                      </Form.Item>
                      <Form.Item
                        name="transaction_id"
                        label="Transaction / Reference No (Optional)"
                      >
                        <Input placeholder="Enter payment reference" />
                      </Form.Item>
                      {isGroupStay && (
                        <div
                          style={{
                            fontSize: 12,
                            color: "#874d00",
                            marginTop: -8,
                            marginBottom: 8,
                          }}
                        >
                          This records ONE payment for the whole group — the
                          amount is already taken, so it will not be collected
                          again at checkout.
                        </div>
                      )}
                    </div>
                  ) : null
                }
              </Form.Item>
            </div>
          )}
        </div>

        <Form.Item
          name="early_check_in"
          label="Early Check-In"
          valuePropName="checked"
        >
          <Checkbox>Guest arriving before standard check-in time</Checkbox>
        </Form.Item>

        <Form.Item
          shouldUpdate={(prev, curr) =>
            prev.early_check_in !== curr.early_check_in
          }
          noStyle
        >
          {({ getFieldValue }) =>
            getFieldValue("early_check_in") ? (
              <Form.Item
                name="early_check_in_fee"
                label="Early Check-In Fee (LKR)"
              >
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            ) : null
          }
        </Form.Item>
      </Form>
    </Modal>
  );
};
